// TemplateManagement.tsx
import React, { useState, useEffect } from 'react';
import { Copy, Edit, Trash2, CheckCircle, PlusCircle, Settings, Lock, Save, X, FileText, AlertTriangle  } from 'lucide-react';
import {Tooltip} from '../ui/Tooltip';
import { CategoryTemplate } from '../../types';

interface TemplateManagementProps {
  templates: CategoryTemplate[];
  activeTemplate: CategoryTemplate | null;
  editingTemplate: CategoryTemplate | null;
  onSetActiveTemplate: (template: CategoryTemplate) => void;
  onSelectTemplateForEditing: (template: CategoryTemplate) => void;
  onTemplateCopy: (template: Partial<CategoryTemplate>) => void;
  onTemplateDelete: (templateId: string) => void;
  onCreateTemplate: (name: string) => void;
  onRenameTemplate?: (templateId: string, newName: string) => Promise<void>;
}

const TemplateManagement = ({
  templates,
  activeTemplate,
  editingTemplate,
  onSetActiveTemplate,
  onSelectTemplateForEditing,
  onTemplateCopy,
  onTemplateDelete,
  onCreateTemplate,
  onRenameTemplate
}: TemplateManagementProps) => {
  const [showCopyDialog, setShowCopyDialog] = useState(false);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showRenameDialog, setShowRenameDialog] = useState(false);
  const [newTemplateName, setNewTemplateName] = useState('');
  const [templateToCopy, setTemplateToCopy] = useState<CategoryTemplate | null>(null);
  const [templateToRename, setTemplateToRename] = useState<CategoryTemplate | null>(null);
  const [isRenameSaving, setIsRenameSaving] = useState(false);

  // Add new state variables for confirmation dialog
  const [showSetActiveConfirmation, setShowSetActiveConfirmation] = useState(false);
  const [templateToSetActive, setTemplateToSetActive] = useState<CategoryTemplate | null>(null);

   // Add a new function to handle the initial Set as Active click
   const handleSetActiveClick = (template: CategoryTemplate) => {
    setTemplateToSetActive(template);
    setShowSetActiveConfirmation(true);
  };
  
  // Function to confirm setting active template
  const confirmSetActive = () => {
    if (templateToSetActive) {
      onSetActiveTemplate(templateToSetActive);
    }
    setShowSetActiveConfirmation(false);
    setTemplateToSetActive(null);
  };
  
  const handleCopy = (template: CategoryTemplate) => {
    setTemplateToCopy(template);
    setNewTemplateName(`${template.name} Copy`);
    setShowCopyDialog(true);
  };
  
  const handleConfirmCopy = () => {
    if (!templateToCopy || !newTemplateName.trim()) return;
    
    onTemplateCopy({
      ...templateToCopy,
      name: newTemplateName.trim(),
      is_default: false
    });
    
    setShowCopyDialog(false);
    setTemplateToCopy(null);
    setNewTemplateName('');
  };

  const handleCreateNew = () => {
    setNewTemplateName('');
    setShowCreateDialog(true);
  };

  const handleConfirmCreate = () => {
    if (!newTemplateName.trim()) return;
    
    onCreateTemplate(newTemplateName.trim());
    setShowCreateDialog(false);
    setNewTemplateName('');
  };

  const handleRename = (template: CategoryTemplate) => {
    if (template.is_default) return; // Cannot rename default templates
    
    setTemplateToRename(template);
    setNewTemplateName(template.name);
    setShowRenameDialog(true);
  };

  const handleConfirmRename = async () => {
    if (!templateToRename || !newTemplateName.trim() || !onRenameTemplate) return;
    
    try {
      setIsRenameSaving(true);
      await onRenameTemplate(templateToRename.id, newTemplateName.trim());
      setShowRenameDialog(false);
      setTemplateToRename(null);
      setNewTemplateName('');
    } catch (error) {
      console.error('Error renaming template:', error);
      alert('Failed to rename template. Please try again.');
    } finally {
      setIsRenameSaving(false);
    }
  };
  
  // Function to format date
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };
  
  return (
    <div className="space-y-6">
      <div className="mb-2">
        <h2 className="text-lg font-medium text-gray-900">Category Template Management</h2>
        <p className="text-sm text-gray-500">
          The active category template controls account category mapping in the Chart of Accounts. You can edit any template without changing the active one.
        </p>
      </div>
      
      {/* Active template section */}
      {activeTemplate && (
        <div className="mb-8">
          <div className="bg-white rounded-lg border border-blue-200 shadow-sm overflow-hidden">
            <div className="p-4 bg-blue-50 border-l-4 border-blue-500">
              <div className="flex justify-between items-center">
                <div>
                  <h4 className="font-medium text-lg text-gray-900">
                    {activeTemplate.name}
                    {activeTemplate.is_default && (
                      <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">
                        <Lock className="h-3 w-3 mr-1" />
                        Default
                      </span>
                    )}
                  </h4>
                  <div className="flex items-center mt-1">
                    <CheckCircle className="h-4 w-4 text-blue-600 mr-1" />
                    <span className="text-sm text-blue-600 font-medium">Active project template</span>
                  </div>
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => onSelectTemplateForEditing(activeTemplate)}
                    className={`inline-flex items-center px-3 py-1.5 border rounded-md text-sm font-medium ${
                      editingTemplate?.id === activeTemplate.id
                        ? 'bg-blue-100 border-blue-300 text-blue-700'
                        : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    {activeTemplate.is_default ? (
                      <>
                        <FileText className="h-4 w-4 mr-2 text-gray-500" />
                        View Categories
                      </>
                    ) : (
                      <>
                        <Settings className={`h-4 w-4 mr-2 ${
                          editingTemplate?.id === activeTemplate.id ? 'text-blue-500' : 'text-gray-500'
                        }`} />
                        {editingTemplate?.id === activeTemplate.id ? 'Currently Editing' : 'Edit Categories'}
                      </>
                    )}
                  </button>
                  <button
                    onClick={() => handleCopy(activeTemplate)}
                    className="inline-flex items-center px-3 py-1.5 bg-white border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    <Copy className="h-4 w-4 mr-2 text-gray-500" />
                    Copy
                  </button>
                  {!activeTemplate.is_default && onRenameTemplate && (
                    <button
                      onClick={() => handleRename(activeTemplate)}
                      className="inline-flex items-center px-3 py-1.5 bg-white border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                    >
                      <svg className="h-4 w-4 mr-2 text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                      Rename
                    </button>
                  )}
                </div>
              </div>
              <div className="mt-2 flex items-center text-sm text-gray-500">
                <span className="mr-3">Last updated: {formatDate(activeTemplate.updated_at)}</span>
                {activeTemplate.is_default && (
                  <span className="text-amber-600 text-xs font-medium flex items-center">
                    <Lock className="h-3 w-3 mr-1" />
                    System Default Template is read-only, but it can be copied and saved as a new template
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Other templates section */}
      <div className="mb-12">
        <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-3">
          Available Category Templates
        </h3>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {templates
            .filter(template => !activeTemplate || template.id !== activeTemplate.id)
            .map(template => (
              <div
                key={template.id}
                className={`relative bg-white rounded-lg border shadow-sm overflow-hidden flex flex-col ${
                  editingTemplate?.id === template.id 
                    ? 'border-blue-300 ring-1 ring-blue-300' 
                    : 'border-gray-200'
                }`}
              >
                {editingTemplate?.id === template.id && (
                  <div className="absolute top-0 right-0 mt-2 mr-2">
                    <span className="px-2 py-0.5 bg-blue-100 text-blue-800 rounded-full text-xs font-medium">
                      Editing
                    </span>
                  </div>
                )}
                <div className="p-4 flex-grow">
                  <h4 className="font-medium text-gray-900 flex items-center">
                    {template.name}
                    {template.is_default && (
                      <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">
                        <Lock className="h-3 w-3 mr-1" />
                        Default
                      </span>
                    )}
                  </h4>
                  <div className="mt-2 text-sm text-gray-500">
                    Last updated: {formatDate(template.updated_at)}
                  </div>
                  {template.is_default && (
                    <div className="mt-1 text-xs text-amber-600">
                      Read-only template (can be copied)
                    </div>
                  )}
                </div>
                <div className="border-t border-gray-200 bg-gray-50 p-3 flex justify-between">
                  <button
                    onClick={() => handleSetActiveClick(template)}
                    className="inline-flex items-center text-sm text-blue-600 font-medium hover:text-blue-800"
                  >
                    <Settings className="h-3.5 w-3.5 mr-1" />
                    Set as Active
                  </button>
                  <div className="flex space-x-3">
                    {/* Action buttons with tooltips for clarity */}
                    <button
                      onClick={() => onSelectTemplateForEditing(template)}
                      className={`text-sm relative group ${
                        editingTemplate?.id === template.id 
                          ? 'text-blue-600' 
                          : 'text-gray-600 hover:text-gray-900'
                      }`}
                      aria-label="Edit categories"
                    >
                      {template.is_default ? (
                        <Lock className="h-4 w-4" />
                      ) : (
                        <Settings className="h-4 w-4" />
                      )}
                      <span className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-1 px-2 py-1 text-xs text-white bg-gray-800 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                        {template.is_default ? "View Categories" : "Edit Categories"}
                      </span>
                    </button>
                    <button
                      onClick={() => handleCopy(template)}
                      className="text-sm text-gray-600 hover:text-gray-900 relative group"
                      aria-label="Copy template"
                    >
                      <Copy className="h-4 w-4" />
                      <span className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-1 px-2 py-1 text-xs text-white bg-gray-800 rounded opacity-0 group-hover:opacity-100 transition-opacity">
                        Copy Template
                      </span>
                    </button>
                    {!template.is_default && onRenameTemplate && (
                      <button
                        onClick={() => handleRename(template)}
                        className="text-sm text-gray-600 hover:text-gray-900 relative group"
                        aria-label="Rename template"
                      >
                        <span className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-1 px-2 py-1 text-xs text-white bg-gray-800 rounded opacity-0 group-hover:opacity-100 transition-opacity">
                          Rename Template
                        </span>
                        <svg className="h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                      </button>
                    )}
                    {!template.is_default && (
                    <Tooltip content="Delete Template" position="auto">
                      <button
                        onClick={() => onTemplateDelete(template.id)}
                        className="text-sm text-red-600 hover:text-red-900"
                        aria-label="Delete template"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </Tooltip>
                    )}
                  </div>
                </div>
              </div>
            ))}
            
          {/* Create new template card */}
          <div
            onClick={handleCreateNew}
            className="relative bg-white rounded-lg border border-dashed border-gray-300 shadow-sm overflow-hidden flex flex-col items-center justify-center p-6 cursor-pointer hover:bg-gray-50 transition-colors duration-200"
          >
            <PlusCircle className="h-10 w-10 text-gray-400 mb-2" />
            <span className="text-sm font-medium text-gray-900">Create New Template</span>
            <span className="text-xs text-gray-500 mt-1">
              Define custom categories for your accounting data
            </span>
          </div>
        </div>
      </div>

      {/* Add Set Active confirmation dialog */}
      {showSetActiveConfirmation && templateToSetActive && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <div className="flex items-start mb-4">
              <div className="flex-shrink-0 text-amber-500 mr-3">
                <AlertTriangle className="h-6 w-6" />
              </div>
              <div>
                <h3 className="text-lg font-medium text-gray-900">Change Active Template?</h3>
                <p className="mt-2 text-sm text-gray-500">
                  Caution: If you set this as the active template, you will need to remap categories in the Chart of Accounts tab and may lose prior mappings.
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button
                onClick={() => setShowSetActiveConfirmation(false)}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={confirmSetActive}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Section divider*/}
      <div className="h-1 bg-blue-500 w-full mb-6"></div>
      
      {/* Copy template dialog */}
      {showCopyDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-medium mb-4">Copy Template</h3>
            <input
              type="text"
              value={newTemplateName}
              onChange={(e) => setNewTemplateName(e.target.value)}
              className="w-full px-3 py-2 border rounded-md mb-4"
              placeholder="Enter template name"
              autoFocus
            />
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setShowCopyDialog(false)}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmCopy}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                disabled={!newTemplateName.trim()}
              >
                Create Copy
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create new template dialog */}
      {showCreateDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-medium mb-4">Create New Template</h3>
            <input
              type="text"
              value={newTemplateName}
              onChange={(e) => setNewTemplateName(e.target.value)}
              className="w-full px-3 py-2 border rounded-md mb-4"
              placeholder="Enter template name"
              autoFocus
            />
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setShowCreateDialog(false)}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmCreate}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                disabled={!newTemplateName.trim()}
              >
                Create Template
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Rename template dialog */}
      {showRenameDialog && templateToRename && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-medium mb-4">Rename Template</h3>
            <input
              type="text"
              value={newTemplateName}
              onChange={(e) => setNewTemplateName(e.target.value)}
              className="w-full px-3 py-2 border rounded-md mb-4"
              placeholder="Enter new template name"
              autoFocus
            />
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setShowRenameDialog(false)}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
                disabled={isRenameSaving}
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmRename}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center"
                disabled={!newTemplateName.trim() || isRenameSaving}
              >
                {isRenameSaving ? (
                  <>Saving...</>
                ) : (
                  <>Rename</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TemplateManagement;
